import { Alert, CheckboxGrid, Clickable, H1, P, TypeWrapper, Wrapper } from '@ahmdigital/ui';
import { concat, includes, map, remove } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import styled from 'styled-components';

import { Maybe, Service } from '../../../../../types/__codegen__/ahm-api/graphql';
import { scrollTo } from '../../../../../utils/ui';
import { STEP_SLUG } from '../../../constants';
import { useByocNavigationContext } from '../../../context/byoc-navigation-context';
import BuildYourCoverActions from '../../../../../actions/build-your-cover';
import selectBuildYourCover from '../../../../../selectors/select-build-your-cover';
import StepFooterNavigation from '../../../components/step-footer-navigation';
import type { BuildYourCoverProductType } from '../../../../../actions/build-your-cover';

type Props = {
  selectedServices: Service['serviceId'][];
  serviceList: Maybe<Service>[];
  setSelectedServices: (selectedServices: Service['serviceId'][]) => void;
  subtitle: string;
  title: string;
};

const AlertSection = styled.div`
  text-align: left;

  @media (min-width: ${({ theme }) => theme.media.md}) {
    width: fit-content;
  }
`;

const StyledClickable = styled(Clickable)`
  margin: 2.5rem 0;
`;

const SLUG_TO_PRODUCT_TYPE: Partial<Record<string, BuildYourCoverProductType>> = {
  [STEP_SLUG.EXTRAS]: 'extras',
  [STEP_SLUG.HOSPITAL]: 'hospital',
};

const initialViewCount = 10;
const revealCount = 10;

const ServiceSelection = ({ selectedServices, serviceList, setSelectedServices, subtitle, title }: Props) => {
  const { activeSlug, analyticsLabel, goToNextStep } = useByocNavigationContext();
  const dispatch = useDispatch();
  const [selections, setSelections] = useState(selectedServices);
  const { selectedProducts } = useSelector(selectBuildYourCover);
  const [toView, setToView] = useState(initialViewCount);
  const [showAlert, setShowAlert] = useState(
    (activeSlug === STEP_SLUG.HOSPITAL && Boolean(selectedProducts.hospital)) ||
      (activeSlug === STEP_SLUG.EXTRAS && Boolean(selectedProducts.extras)),
  );

  const handleCloseAlert = () => setShowAlert(false);

  const toggleSelection = (service: string) => {
    if (includes(service, selections)) {
      setSelections(remove((selection) => selection === service, selections));
    } else {
      setSelections(concat([service], selections));
    }
  };

  const viewMore = () => {
    setToView(Math.min(toView + revealCount, serviceList.length));
  };
  const backToTop = () => {
    scrollTo(0);
  };

  const handleOnNextClick = () => {
    setSelectedServices(selections);
    if (!selections.length) {
      const productType = SLUG_TO_PRODUCT_TYPE[activeSlug];
      if (productType) {
        BuildYourCoverActions.skipStep(productType)(dispatch);
      }
    }
    goToNextStep();
  };

  const items = map(
    (service) => ({
      analytics: {
        label: `Selected ${analyticsLabel} service`,
      },
      label: service?.name,
      tooltipContent: service?.description,
      value: service?.serviceId,
    }),
    serviceList,
  );

  return (
    <>
      <Wrapper size="medium">
        <TypeWrapper variant="center">
          <H1 looksLike="h2">{title}</H1>
          <P variant="paddingBottomSpacer8">{subtitle}</P>
          <AlertSection>
            {showAlert && (
              <Alert
                heading="Your cover may change"
                analyticsLabel="Cover may change"
                level="info"
                onClose={handleCloseAlert}
              >
                <P>Updating your hospital or extras services may remove some of your previously selected cover.</P>
              </Alert>
            )}
          </AlertSection>
          {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ analytics: { label: string; }; label: string; tooltipContent: Maybe<string> | undefined; value: string; }[]' is not assignable to type 'Items[]'. */}
          <CheckboxGrid checkedValues={selections} items={items.slice(0, toView)} onChange={toggleSelection} />

          {toView < serviceList.length ? (
            <StyledClickable onClick={viewMore} variant="button" icon={{ name: 'chevron-down', position: 'right' }}>
              View more
            </StyledClickable>
          ) : (
            <StyledClickable onClick={backToTop} variant="button" icon={{ name: 'chevron-up', position: 'right' }}>
              Back to top
            </StyledClickable>
          )}
        </TypeWrapper>
      </Wrapper>
      <StepFooterNavigation onNextClick={handleOnNextClick} />
    </>
  );
};

export default ServiceSelection;
